import { Globe } from 'lucide-react'
import { useLocationAccess } from '@/hooks/useLocationAccess'
import { useAppContext, useLocationNavigation } from '@/hooks/routing'
import { useAllLocationStats, getStatsForLocation } from '@/hooks/useLocationStats'
import { getLocationColor, NETWORK_COLOR } from '@/lib/locationColors'
import { normalizeSlug } from '@/lib/slugs'
import { useSidebarStore } from '@/stores/sidebarStore'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { BrandLogo } from './BrandLogo'

function RailButton({
  active,
  color,
  onClick,
  tooltip,
  badge,
  children,
}: {
  active: boolean
  color: string
  onClick: () => void
  tooltip: React.ReactNode
  badge?: number
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          className={cn(
            'relative flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg border-2 transition-all',
            active
              ? 'bg-[var(--rail-active-bg)] border-[var(--rail-active-border)]'
              : 'border-transparent hover:bg-sidebar-accent'
          )}
          style={
            {
              '--rail-active-bg': `${color}15`,
              '--rail-active-border': `${color}35`,
            } as React.CSSProperties
          }
        >
          {/* Active indicator bar */}
          {active && (
            <div
              className="absolute -left-[7px] top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-sm"
              style={{
                background: color,
                boxShadow: `0 0 6px ${color}50`,
              }}
            />
          )}
          {children}
          {/* Conflict badge */}
          {badge != null && badge > 0 && (
            <div className="absolute -right-px -top-px flex h-[13px] w-[13px] items-center justify-center rounded-full border-2 border-sidebar bg-red-600 text-[7px] font-bold text-white">
              {badge}
            </div>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" align="center" sideOffset={8}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

export function IconRail() {
  const { railVisible } = useSidebarStore()
  const { accessibleLocations, hasMultipleLocations } = useLocationAccess()
  const { scope, locationSlug } = useAppContext()
  const { switchLocation, goToOrgView } = useLocationNavigation()
  const { data: allStats } = useAllLocationStats()

  const isNetwork = scope === 'org'

  if (!hasMultipleLocations || !railVisible) return null

  return (
    <div className="flex w-[54px] shrink-0 flex-col items-center border-r border-sidebar-border bg-sidebar pt-2 transition-all duration-300">
      {/* Brand icon */}
      <div className="mb-1.5 shrink-0">
        <BrandLogo size="md" showText={false} />
      </div>

      {/* Network button */}
      <RailButton
        active={isNetwork}
        color={NETWORK_COLOR}
        onClick={() => goToOrgView({ resource: 'dashboard' })}
        tooltip="Network Overview"
      >
        <Globe
          className={cn('h-4 w-4', !isNetwork && 'text-muted-foreground')}
          style={isNetwork ? { color: NETWORK_COLOR } : undefined}
          strokeWidth={1.5}
        />
      </RailButton>

      {/* Divider */}
      <div className="my-1 h-px w-[22px] shrink-0 bg-border" />

      {/* Location buttons */}
      <div className="flex flex-1 flex-col items-center gap-0.5 overflow-y-auto overflow-x-hidden pb-2">
        {accessibleLocations.map((loc) => {
          const slug = normalizeSlug(loc.location_code)
          const isActive = !isNetwork && locationSlug === slug
          const color = getLocationColor(loc.location_code)
          const stats = getStatsForLocation(allStats, loc.id)
          const code = loc.iata_code || loc.location_code?.slice(0, 3).toUpperCase() || '???'

          return (
            <RailButton
              key={loc.id}
              active={isActive}
              color={color}
              onClick={() => switchLocation(slug)}
              badge={stats?.conflictCount}
              tooltip={
                <div className="flex flex-col">
                  <span className="font-medium">{loc.name}</span>
                  {stats && (
                    <span className="text-xs text-muted-foreground">
                      {stats.aircraftCount} a/c &middot; {stats.occupancyPercent}%
                    </span>
                  )}
                </div>
              }
            >
              <span
                className={cn('font-mono text-[10px] font-bold tracking-tight transition-colors', !isActive && 'text-muted-foreground')}
                style={isActive ? { color } : undefined}
              >
                {code}
              </span>
            </RailButton>
          )
        })}
      </div>
    </div>
  )
}
